import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=auth
  const [loading, setLoading] = useState(true);

  // Session lives in a secure httpOnly cookie — verify it via /auth/me.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Presence heartbeat — mark the user online while logged in.
  useEffect(() => {
    if (!user || user === false) return;
    let stopped = false;
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping();
    const id = setInterval(() => { if (!stopped) ping(); }, 30000);
    return () => { stopped = true; clearInterval(id); };
  }, [user]);

  // Cookie is already set by the backend on the auth response; just store user.
  const loginWithToken = useCallback((_token, userObj) => {
    setUser(userObj);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout request failed; clearing session client-side", e?.message);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginWithToken, logout, refresh, setUser }),
    [user, loading, loginWithToken, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
